import type React from 'react';

import { Translate, TranslateHtml } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Input } from '@change-corgi/design-system-legacy/components/forms';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { useTrackView } from 'src/app/shared/hooks/tracking';

import { useConfirmVanityUrlForm } from './hooks/useConfirmVanityUrlForm';

const Errors: React.FC<{ errors: string[] }> = ({ errors }) => {
	return (
		<>
			{errors.map((error) => (
				<p key={error}>{error}</p>
			))}
		</>
	);
};

type Props = {
	closeModal: ({ status, url }: { status: 'success'; url: string }) => void;
	petitionSlug: string;
	petitionId: string;
	displayedSignatures: number;
};

export const ConfirmVanityUrlForm: React.FC<Props> = ({
	closeModal,
	petitionSlug,
	petitionId,
	displayedSignatures,
}) => {
	const { resolveUrl } = useUtilityContext();
	const {
		data: { loading, urlError, vanityUrl },
		actions: { handleEdit, handleSubmit },
	} = useConfirmVanityUrlForm({ closeModal, petitionSlug, petitionId, displayedSignatures });

	useTrackView('custom_url_confirm_view', {
		petition_id: `petition_${petitionId}`,
		num_signatures: displayedSignatures,
	});

	return (
		<Box>
			<Text as="p" mt="16px" id="vanity-modal-description">
				<TranslateHtml value="corgi.petition.starter_dashboard.grow_petition.copy_link.confirm_link_description" />
			</Text>
			<form onSubmit={handleSubmit}>
				<Box sx={{ flexGrow: 1, mt: 24 }}>
					<Input
						aria-labelledby="vanity-modal-title"
						aria-describedby="vanity-modal-description"
						id="confirm-url-input"
						name="confirm-url-input"
						type="text"
						maxLength={100}
						minLength={10}
						error={urlError ? <Errors errors={urlError} /> : null}
						value={`${resolveUrl('/')}${vanityUrl}`}
						disabled
						sx={{ borderColor: 'neutral-grey100', backgroundColor: 'primary-greyBackground' }}
					/>
				</Box>
				<Flex sx={{ gap: '16px', justifyContent: 'flex-end', mt: '16px', mr: '0px' }}>
					<Button
						data-testid="customUrl-edit-button"
						data-qa="customUrl-edit-button"
						variant="secondary"
						type="button"
						onClick={handleEdit}
					>
						<Translate value="corgi.petition.starter_dashboard.grow_petition.copy_link.edit_link" />
					</Button>
					<Button
						data-testid="customUrl-create-confirm"
						data-qa="customUrl-create-confirm"
						variant="primary"
						type="submit"
						loading={loading}
					>
						<Translate value="corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_cta" />
					</Button>
				</Flex>
			</form>
		</Box>
	);
};

export const ConfirmVanityUrlFormTitle: React.FC = () => {
	return (
		<Heading as="h1" size="h3" id="vanity-modal-title" mt="48px">
			<Translate value="corgi.petition.starter_dashboard.grow_petition.copy_link.confirm_link_title" />
		</Heading>
	);
};
